import { alpha } from '@mui/material/styles';

import { COLORS, IColors } from './colors';

interface IGradientTwoColors {
  position?: string;
  color1?: string;
  opacity1?: string;
  color2?: string;
  opacity2?: string;
}

interface IGradientThreeColors {
  position?: string;
  color1?: string;
  opacity1?: string;
  color2?: string;
  opacity2?: string;
  color3?: string;
  opacity3?: string;
}

interface IChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

interface IGradientsPaletteOptions {
  white: string;
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
  chatGPT: string;
  leftContentLogin: string;
  genAIButton: string;
  advisorGradient: string;
}

declare module '@mui/material/styles/createPalette' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface CommonColors {
    human: string;
    humanText?: string;
    specialized: string;
    humanContrast: string;
    secondHumanContrast: string;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface TypeText extends IColors {
    main: string;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface TypeBackground extends IColors {
    main: string;
    neutral: string;
    ikigaiGray: string;
    ikigaiMain: string;
    ikigaiYellow: string;
    ikigaiOrange: string;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Palette {
    icons: IColors;
    borders: IColors;
    boxShadows: IColors;
    linearProgress: IColors;
    circularProgress: IColors;
    checkboxs: IColors;
    chart: IChartPaletteOptions & IColors;
    gradients: IGradientsPaletteOptions;
  }
}

declare module '@mui/material' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

const gradientTwoColors: (Props: IGradientTwoColors) => string = ({ position, color1, opacity1, color2, opacity2 }) => {
  const color1Opacity = opacity1 ? `${color1} ${opacity1}` : color1;
  const color2Opacity = opacity2 ? `${color2} ${opacity2}` : color2;

  return `linear-gradient(${position}, ${color1Opacity}, ${color2Opacity})`;
};

const gradientThreeColors: (Props: IGradientThreeColors) => string = ({
  position,
  color1,
  opacity1,
  color2,
  opacity2,
  color3,
  opacity3,
}) => `linear-gradient(${position}, ${color1} ${opacity1}, ${color2} ${opacity2}, ${color3} ${opacity3})`;

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const PRIMARY = {
  lighter: '#ebb7dc',
  light: '#db57ad',
  main: COLORS.redViolet,
  dark: '#b00076',
  darker: '#720062',
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#bbddf8',
  light: '#67b4f0',
  main: COLORS.moonBlue,
  dark: '#1f75ca',
  darker: COLORS.moonBlue,
  contrastText: '#fff',
};

const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#B1EBAA',
  light: '#81DE77',
  main: '#62D655',
  dark: '#45963b',
  darker: '#08660D',
  contrastText: GREY[800],
};

const WARNING = {
  lighter: '#FED4B5',
  light: '#FE9A52',
  main: '#FD6F08',
  dark: '#CA5906',
  darker: '#7A4F01',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFA4A1',
  light: '#FF6D68',
  main: '#FF4842',
  dark: '#CC3A35',
  darker: '#CC3A35',
  contrastText: '#fff',
};

const CHARTS = {
  blue: ['blue'],
  green: ['green'],
  red: ['red'],
  violet: ['violet'],
  yellow: ['yellow'],
  ...COLORS,
};

const GRADIENTS = {
  white: gradientTwoColors({ position: '90deg', color1: 'white', color2: 'white' }),
  primary: gradientThreeColors({ color1: '#fff' }),
  info: gradientThreeColors({ color1: '#fff' }),
  success: gradientThreeColors({ color2: '#fff' }),
  warning: gradientThreeColors({ color3: '#fff' }),
  error: gradientThreeColors({ color3: '#fff' }),
  advisorGradient: gradientThreeColors({
    position: '113.7deg',
    color1: COLORS.frenchRose,
    opacity1: '0%',
    color2: COLORS.yaleBlue,
    opacity2: '52.46%',
    color3: COLORS.pictonBlue,
    opacity3: '94.14%',
  }),
  chatGPT: gradientThreeColors({
    position: '113.7deg',
    color1: COLORS.frenchRose,
    opacity1: '0%',
    color2: COLORS.yaleBlue,
    opacity2: '52.46%',
    color3: COLORS.pictonBlue,
    opacity3: '94.14%',
  }),
  leftContentLogin: gradientTwoColors({
    position: '135deg',
    color1: COLORS.yaleBlue,
    opacity1: '-20%',
    color2: COLORS.frenchRose,
    opacity2: '100%',
  }),
  genAIButton: gradientThreeColors({
    position: '90deg',
    color1: COLORS.firstGenAIButton,
    opacity1: '0%',
    color2: COLORS.secondGenAIButton,
    opacity2: '50%',
    color3: COLORS.thirdGenAIButton,
    opacity3: '100%',
  }),
};

const COMMON = {
  common: {
    black: '#000',
    white: '#fff',
    human: COLORS.frenchRose,
    humanContrast: COLORS.contrastRedViolet,
    secondHumanContrast: COLORS.secondHumanContrast,
    specialized: COLORS.moonBlue,
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  icons: { ...COLORS, main: COLORS.prussianBlue },
  borders: { ...COLORS, main: COLORS.prussianBlue },
  boxShadows: { ...COLORS },
  linearProgress: { ...COLORS },
  circularProgress: {
    ...COLORS,
  },
  checkboxs: { ...COLORS },
  chart: { ...COLORS, ...CHARTS },
  gradients: GRADIENTS,
  grey: GREY,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    text: {
      ...COLORS,
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      ...COLORS,
      paper: '#fff',
      default: '#fff',
      neutral: GREY[200],
      ikigaiGray: '#B0B9C0',
      ikigaiMain: '#D82B9A',
      ikigaiYellow: '#FECC01',
      ikigaiOrange: '#F7A70D',
    },
    action: { active: GREY[600] },
  },
  dark: {
    ...COMMON,
    text: {
      ...COLORS,
      primary: '#fff',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      ...COLORS,
      paper: GREY[800],
      default: GREY[900],
      neutral: GREY[500_16],
      ikigaiGray: '#B0B9C0',
      ikigaiMain: '#D82B9A',
      ikigaiYellow: '#FECC01',
      ikigaiOrange: '#F7A70D',
    },
    action: { active: GREY[500] },
  },
};

export default palette;
